import { useState, useEffect, useRef, useCallback } from 'react';
import { useInterval } from './useInterval';
import { supabase } from '../supabaseClient';

const fetchLightningData = async (site) => {
  let lightningData = JSON.parse(localStorage.getItem(`${site}_lastLightningData`) || '[]');

  try {
    const now = new Date();
    const thirtyMinutesAgo = new Date(now - 30 * 60 * 1000).toISOString();
    const { data: fetched, error } = await supabase
      .from('ivanhoe_weather_lightning')
      .select('site, latitude, longitude, time_utc, peakamp, numsensors, distance_km, bearing, bearing_eng')
      .eq('site', site)
      .gte('time_utc', thirtyMinutesAgo)
      .lte('time_utc', now.toISOString())
      .order('time_utc', { ascending: true })
      .limit(5000);

    if (error) {
      throw error;
    }

    if (Array.isArray(fetched)) {
      lightningData = fetched;
      localStorage.setItem(`${site}_lastLightningData`, JSON.stringify(lightningData));
    } else {
      console.warn("Lightning data is not an array. Using cached data if available.");
    }
  } catch (err) {
    console.error(`Error fetching lightning data for ${site}: ${err.message}`);
  }

  return lightningData;
};

export const useLatestLightningData = (site, refreshInterval = 5, enabled = true) => {
  const [lightningData, setLightningData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    if (!site) {
      setError('Site is required');
      setLoading(false);
      return;
    }

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    setLoading(true);
    setError(null);

    try {
      const data = await fetchLightningData(site);
      if (requestId !== requestIdRef.current) {
        return;
      }
      setLightningData(data);
    } catch (err) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      setError(`Failed to fetch lightning data: ${err.message}`);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [site, enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [fetchData, refreshInterval, enabled]);

  useInterval(() => {
    if (enabled && refreshInterval > 0) {
      fetchData();
    }
  }, enabled && refreshInterval > 0 ? refreshInterval * 60 * 1000 : null);

  return { lightningData, loading, error, fetchData };
};
